import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, MessageSquareText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { GoogleG } from "@/components/common/GoogleBadge";
import { business } from "@/data/site";
import { cn } from "@/lib/utils";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/brands", label: "Brands" },
  { to: "/service-area", label: "Service Area" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-colors duration-300",
        scrolled
          ? "bg-background/90 backdrop-blur-md border-b border-border shadow-soft"
          : "bg-background border-b border-transparent"
      )}
    >
      <div className="mx-auto flex h-18 max-w-7xl items-center justify-between gap-4 py-3 container-px">
        <Link to="/" className="flex items-center gap-3">
          <img
            src={business.logo}
            alt={`${business.legalName} logo`}
            className="h-12 w-12 rounded-lg object-cover ring-1 ring-border"
          />
          <span
            className="font-display text-lg font-bold leading-none text-foreground sm:text-xl"
            style={{ WebkitTextStroke: "1px hsl(var(--primary))", paintOrder: "stroke fill" }}
          >
            JRs Chair Repair
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                cn(
                  "rounded-md px-3.5 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="outline"
            className="hidden gap-2 sm:inline-flex"
          >
            <a
              href={business.google}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Check us out on Google"
            >
              <GoogleG className="h-4 w-4" />
              <span className="hidden lg:inline">Reviews</span>
            </a>
          </Button>

          <Button asChild variant="outline" size="icon" className="sm:hidden" aria-label="Check us out on Google">
            <a href={business.google} target="_blank" rel="noopener noreferrer">
              <GoogleG className="h-5 w-5" />
            </a>
          </Button>

          <Button asChild variant="brand" className="hidden sm:inline-flex">
            <a href={business.phoneHref}>
              <Phone className="h-4 w-4" /> {business.phone}
            </a>
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="flex w-[300px] flex-col bg-background p-0">
              <SheetTitle className="sr-only">Main menu</SheetTitle>
              <div className="flex items-center gap-3 border-b border-border p-5">
                <img src={business.logo} alt="" className="h-10 w-10 rounded-md object-cover" />
                <span className="font-display text-lg font-bold">
                  JR<span className="text-primary">s</span>{" "}
                  <span className="text-xs font-medium text-muted-foreground">Chair Repair</span>
                </span>
              </div>
              <nav className="flex flex-1 flex-col overflow-y-auto p-3">
                {navLinks.map((link) => (
                  <SheetClose asChild key={link.to}>
                    <NavLink
                      to={link.to}
                      end={link.to === "/"}
                      className={({ isActive }) =>
                        cn(
                          "rounded-md px-4 py-3 text-base font-medium transition-colors",
                          isActive
                            ? "bg-primary/10 text-foreground"
                            : "text-muted-foreground hover:bg-muted"
                        )
                      }
                    >
                      {link.label}
                    </NavLink>
                  </SheetClose>
                ))}
              </nav>
              <div className="mt-auto space-y-2 border-t border-border p-4">
                <Button asChild variant="brand" className="w-full" size="lg">
                  <a href={business.phoneHref}><Phone className="h-4 w-4" /> Call {business.phone}</a>
                </Button>
                <Button asChild variant="outlineDark" className="w-full" size="lg">
                  <a href={business.smsHref}><MessageSquareText className="h-4 w-4" /> Text Us</a>
                </Button>
                <Button asChild variant="outline" className="w-full gap-2" size="lg">
                  <a href={business.google} target="_blank" rel="noopener noreferrer">
                    <GoogleG className="h-4 w-4" /> Check us out on Google
                  </a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
